<template>
  <hel-module title="销售业务阶段概览" :class="scope.clsPrefix" v-loading="loading">
    <template slot="extra">
      <span class="stage-total">{{ stages.length }} 个阶段</span>
    </template>

    <div class="stage-subtitle">从客户经营到履约交付，查看当前业务分布</div>
    <div class="stage-scroll">
      <div class="stage-item" v-for="item in stages" :key="item.index">
        <div class="stage-index">{{ item.index }}</div>
        <div class="stage-main">
          <div class="stage-title-row">
            <span class="stage-name">{{ item.name }}</span>
            <span class="stage-desc">{{ item.desc }}</span>
          </div>
          <div class="stage-system-row">
            <i class="stage-system-icon"></i>
            <span class="stage-system">{{ item.system }}</span>
          </div>
          <div class="stage-bar">
            <div class="stage-bar-fill" :style="{ width: item.progress + '%' }"></div>
          </div>
        </div>
        <div class="stage-side">
          <div class="stage-count-row">
            <span class="stage-count">{{ item.count }}</span>
            <span class="stage-count-label">{{ item.countUnit }}</span>
            <span class="stage-arrow">›</span>
          </div>
          <div class="stage-amount">{{ item.amount }}</div>
        </div>
      </div>
      <div v-if="!stages.length" class="stage-empty">暂无数据</div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import { getSalesStageOverview } from '@/api/sales'

// 阶段静态元数据：名称/描述/系统/进度等文案由前端维护，数量与金额来自接口
// stageCode：与后端阶段编码对齐；progress：阶段进度条百分比（0-100）
const STAGE_META = [
  { index: '01', stageCode: 'CUSTOMER', name: '客户经营', desc: 'S/A级客户与商机线索', system: 'CRM系统', progress: 15, countUnit: '家重点客户' },
  { index: '02', stageCode: 'QUOTE', name: '商机报价', desc: '另有 1 个项目待测算', system: '报价系统', progress: 55, countUnit: '个已出价项目' },
  { index: '03', stageCode: 'BID', name: '投标作单', desc: '解析 / 写作 / 合规检查', system: '投标系统', progress: 92, countUnit: '个在投项目' },
  { index: '04', stageCode: 'CONTRACT', name: '合同签署', desc: '签约前检查 / 待签署 / 待核验', system: '合同系统', progress: 50, countUnit: '份待处理合同' },
  { index: '05', stageCode: 'DELIVERY', name: '履约交付', desc: '2 个订单存在风险', system: 'ERP交付', progress: 25, countUnit: '个在途订单' }
]

export default {
  name: 'SalesStageOverview',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-sales-stage-overview'
      },
      stages: []
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    // 数据来源：销售业务阶段概览接口，返回阶段列表
    async fetchStageList() {
      const res = await getSalesStageOverview()
      if (!res || res.code !== 200 || !Array.isArray(res.data)) {
        return []
      }
      return res.data
    },
    // 金额展示为「x 万」，无来源时显示 --
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === '') return '--'
      const num = Number(amount)
      if (isNaN(num)) return '--'
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + ' 万'
    },
    async fetchData() {
      this.loading = true
      try {
        const list = await this.fetchStageList()
        const dataByCode = {}
        list.forEach((item) => {
          dataByCode[item.stageCode] = item
        })
        // 静态文案与接口数量/金额按 stageCode 合并
        this.stages = STAGE_META.map((meta) => {
          const item = dataByCode[meta.stageCode] || {}
          return {
            ...meta,
            count: item.itemCount === null || item.itemCount === undefined ? '--' : item.itemCount,
            amount: this.formatAmount(item.amount)
          }
        })
      } catch (error) {
        console.error('获取销售业务阶段概览数据失败:', error)
        this.stages = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-sales-stage-overview";
.#{$class-prefix} {
  .stage-total {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    white-space: nowrap;
  }
  .stage-subtitle {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    margin-bottom: 8px;
  }
  .stage-scroll {
    height: calc(100% - 26px);
    overflow: auto;
  }
  .stage-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f3fa;
    &:last-child {
      border-bottom: none;
    }
  }
  .stage-index {
    flex-shrink: 0;
    width: 28px;
    font-size: 14px;
    font-family: PingFangSC-Semibold;
    font-weight: 700;
    color: #4a5ff3;
  }
  .stage-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .stage-title-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  .stage-name {
    font-size: 13px;
    font-family: PingFangSC-Medium;
    font-weight: 600;
    color: #333333;
    white-space: nowrap;
  }
  .stage-desc {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .stage-system-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .stage-system-icon {
    width: 12px;
    height: 12px;
    border: 1px solid #b6c2d9;
    border-radius: 50%;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 2px;
      width: 1px;
      height: 4px;
      background: #b6c2d9;
      transform: translateX(-50%);
      transform-origin: bottom center;
      rotate: 45deg;
    }
  }
  .stage-system {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    white-space: nowrap;
  }
  .stage-bar {
    height: 4px;
    border-radius: 2px;
    background: #edf0f7;
    overflow: hidden;
  }
  .stage-bar-fill {
    height: 100%;
    border-radius: 2px;
    background: #4a5ff3;
  }
  .stage-side {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }
  .stage-count-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }
  .stage-count {
    font-size: 16px;
    font-family: PingFangSC-Semibold;
    font-weight: 700;
    color: #4a5ff3;
  }
  .stage-count-label {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #606266;
    white-space: nowrap;
  }
  .stage-arrow {
    font-size: 12px;
    color: #b6c2d9;
  }
  .stage-amount {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    white-space: nowrap;
  }
  .stage-empty {
    padding: 16px 4px;
    font-size: 12px;
    color: #98a1b2;
  }
}
</style>
